import { flow } from 'es-toolkit';
import PropTypes from 'prop-types';
import { Component } from 'react';
import { connect } from 'react-redux';
import { selectHasRouteError } from 'src/common/selectors/app';
import { selectIsNowPlayingDialogOpen } from 'src/common/selectors/dialog';
import { useAdEligibilityConfig } from 'src/common/utils/ads/determineAdEligibility';
import { v4 as uuidv4 } from 'uuid';
import { apiBeta } from '../../../client/api';
import { mintSingleton } from '../../../client/mint';
import { withReactHooks } from '../withReactHooks';

function mapStateToProps(state) {
  return {
    isMintInitialized: state.mint.isInitialized,
    hasRouteError: selectHasRouteError(state),
    isNowPlayingDialogOpen: selectIsNowPlayingDialogOpen(state),
  };
}

/**
 * Factory that creates a HOC to manage ad slot registration/requests in Mint
 *
 * @param {object} options
 *  @property {boolean} autoPlay - determines whether slots should be requested by the HOC or by an
 *    external process
 *  @property {boolean} refreshOnPageChange - determines whether slots should be refreshed when the
 *    user navigates to a different page
 */
export default function withAdRegistration(options) {
  const { autoPlay, refreshOnPageChange } = options;
  const isDisplayAd = Boolean(refreshOnPageChange);

  /**
   * HOC that manages ad slot registration/requests in Mint.
   *
   * @param {string} slotName - the name of the slot, as defined by the ad config
   *  (store.config.ads.adConfig)
   * @param {string} id - the unique ID of the HTML element where the ad will render
   * @param {string} [dimensions] - a string representing the specific dimensions required for this
   *  ad slot (e.g., "320x50")
   * @param {string} matchUrl - used to determine page change. Defined in
   *  withCurrentRouteCustomProps.js
   * @param {boolean} deregisterDisplaySlot - deregisters display ads when specific dialogs open.
   *  See store.mintSingleton.instance?.deregisterDisplayAds
   * @param {boolean} isMintInitialized - triggered by WithAds.js after Mint initializes
   *  user explicitly selects consent preferences
   */
  return function withAdRegistrationInner(BaseComponent) {
    class WithAdRegistration extends Component {
      static propTypes = {
        slotName: PropTypes.string,
        slotNames: PropTypes.array,
        id: PropTypes.string.isRequired,
        dimensions: PropTypes.string,
        matchUrl: PropTypes.string,
        deregisterDisplaySlot: PropTypes.bool,
        handleAdDeregistration: PropTypes.func,
        isInDialog: PropTypes.bool,

        // mapStateToProps
        isMintInitialized: PropTypes.bool,
      };

      static registeredComponentsBySlot = new Map();
      static lastRequestTimeBySlotId = new Map();

      #componentId = uuidv4();

      constructor(props) {
        super(props);

        this.slotNames = props.slotNames || [props.slotName];
      }

      get canShowDisplayAd() {
        const { displayAdEligibiltyConfig, isInDialog } = this.props;

        return isInDialog
          ? displayAdEligibiltyConfig.isNowPlayingAdEligible
          : displayAdEligibiltyConfig.isPageAdEligible;
      }

      componentDidMount() {
        const { isMintInitialized, hasRouteError } = this.props;

        if (isMintInitialized && !hasRouteError && (!isDisplayAd || this.canShowDisplayAd)) {
          this.registerAndRequestSlots();
        }
      }

      componentDidUpdate(prevProps) {
        const {
          matchUrl,
          isMintInitialized,
          deregisterDisplaySlot,
          hasRouteError,
          isNowPlayingDialogOpen,
          isInDialog,
        } = this.props;

        if (!isMintInitialized) {
          return;
        }

        if (hasRouteError || (isDisplayAd && !this.canShowDisplayAd)) {
          this.deregisterSlot();
          return;
        }

        if (
          !prevProps.isMintInitialized ||
          (refreshOnPageChange && matchUrl !== prevProps.matchUrl) ||
          (!deregisterDisplaySlot && prevProps.deregisterDisplaySlot)
        ) {
          this.registerAndRequestSlots();
          return;
        }

        if (deregisterDisplaySlot && !prevProps.deregisterDisplaySlot) {
          this.deregisterSlot();
          return;
        }

        // Toggle display ad when NP opens/closes. `refreshOnPageChange` is enabled for display ads only.
        if (
          isDisplayAd &&
          !isInDialog &&
          isNowPlayingDialogOpen !== prevProps.isNowPlayingDialogOpen
        ) {
          if (isNowPlayingDialogOpen) {
            this.deregisterSlot();
          } else {
            this.registerAndRequestSlots();
          }
        }
      }

      componentWillUnmount() {
        this.deregisterSlot();
      }

      async registerAndRequestSlots() {
        const { id, dimensions } = this.props;

        if (isDisplayAd && !this.canShowDisplayAd) {
          return;
        }

        const registeredComponentId = WithAdRegistration.registeredComponentsBySlot.get(id);

        // Prevents race conditions between ad slots with the same id/slot names registering/deregistering.
        // In the upcoming Display Ad library, we'll likely either handle this by requiring each slot to have a
        // unique id or by implementing a similar gating mechanism.
        if (registeredComponentId) {
          this.deregisterSlot();
        }

        WithAdRegistration.registeredComponentsBySlot.set(id, this.#componentId);
        await Promise.all(
          this.slotNames.map((slotName) =>
            mintSingleton.instance?.registerSlot(id, slotName, dimensions),
          ),
        );

        if (autoPlay) {
          const now = Date.now();
          const lastRequestTime = WithAdRegistration.lastRequestTimeBySlotId.get(id);
          const url = typeof window !== 'undefined' && window.location ? window.location.href : '';

          apiBeta.telemetry.logInfo({
            message: 'Display ad requested by WithAdRegistration HOC',
            context: {
              slotId: id,
              url,
              requestTimestampMs: now,
              elapsedMsSincePreviousRequest:
                typeof lastRequestTime === 'number' ? now - lastRequestTime : null,
            },
          });

          WithAdRegistration.lastRequestTimeBySlotId.set(id, now);
          mintSingleton.instance?.requestSlot(id);
        }
      }

      deregisterSlot() {
        const { id } = this.props;

        const registeredComponentId = WithAdRegistration.registeredComponentsBySlot.get(id);

        // Don't deregister ad that isn't owned by this component.
        // Prevents race conditions between ad slots with the same id/slot names registering/deregistering.
        // In the upcoming Display Ad library, we'll likely either handle this by requiring each slot to have a
        // unique id or by implementing a similar gating mechanism.
        if (registeredComponentId !== this.#componentId) {
          return;
        }

        mintSingleton.instance?.deregisterSlot(id);
        this.props?.handleAdDeregistration?.();
      }

      render() {
        return <BaseComponent {...this.props} />;
      }
    }

    return flow(
      connect(mapStateToProps),
      withReactHooks(() => ({
        displayAdEligibiltyConfig: useAdEligibilityConfig(),
      })),
    )(WithAdRegistration);
  };
}
